import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";

export interface StepContentProps {
  title?: string | null;
  body: string;
  mediaUrl?: string | null;
  headerSlot?: ReactNode;
  // P23C: chapter steps render as a centered card — larger title, no headerSlot.
  isChapter?: boolean;
}

export function StepContent({ title, body, mediaUrl, headerSlot, isChapter }: StepContentProps) {
  if (isChapter) {
    return (
      <div className="space-y-4 text-center py-2" data-testid="tour-step-content" data-step-type="chapter">
        {headerSlot ? (
          <div className="flex justify-end items-center pr-8 -mt-3 min-h-[28px]">{headerSlot}</div>
        ) : null}
        {title ? (
          <div
            className="text-2xl font-bold text-foreground leading-tight"
            data-testid="text-tour-step-title"
          >
            {title}
          </div>
        ) : null}
        <div
          className="text-sm leading-relaxed text-foreground [&_a]:underline [&_a]:text-primary [&_strong]:font-semibold [&_em]:italic [&_code]:rounded [&_code]:bg-muted [&_code]:px-1"
          data-testid="text-tour-step-body"
        >
          <ReactMarkdown
            rehypePlugins={[rehypeSanitize]}
            components={{
              a: ({ node: _node, ...props }) => (
                <a {...props} target="_blank" rel="noreferrer noopener" />
              ),
            }}
          >
            {body}
          </ReactMarkdown>
        </div>
        {mediaUrl ? (
          <img
            src={mediaUrl}
            alt=""
            className="mt-2 max-h-56 w-auto rounded-md mx-auto"
            data-testid="img-tour-step-media"
          />
        ) : null}
      </div>
    );
  }

  const showHeaderRow = !!title || !!headerSlot;
  return (
    <div className="space-y-2" data-testid="tour-step-content">
      {showHeaderRow ? (
        <div className="flex items-start justify-between gap-2">
          {title ? (
            <div
              className="text-base font-semibold text-foreground"
              data-testid="text-tour-step-title"
            >
              {title}
            </div>
          ) : (
            <span />
          )}
          {headerSlot ? (
            <div className="flex items-center gap-1 shrink-0">{headerSlot}</div>
          ) : null}
        </div>
      ) : null}
      <div
        className="text-sm text-foreground [&_a]:underline [&_a]:text-primary [&_strong]:font-semibold [&_em]:italic [&_code]:rounded [&_code]:bg-muted [&_code]:px-1"
        data-testid="text-tour-step-body"
      >
        <ReactMarkdown
          rehypePlugins={[rehypeSanitize]}
          components={{
            a: ({ node: _node, ...props }) => (
              <a {...props} target="_blank" rel="noreferrer noopener" />
            ),
          }}
        >
          {body}
        </ReactMarkdown>
      </div>
      {mediaUrl ? (
        <img
          src={mediaUrl}
          alt=""
          className="mt-2 max-h-48 w-auto rounded-md"
          data-testid="img-tour-step-media"
        />
      ) : null}
    </div>
  );
}
